import { useEffect, useState } from 'react'
import { FormSheet, SectionBand } from '../components/FormSheet'
import { listComplaints, officialDate, type Complaint } from '../lib/complaints'

/**
 * The whole docket, most recent first. Nothing links here -- the path is the
 * only thing keeping it private.
 */
export function DocketPage() {
  const [complaints, setComplaints] = useState<Complaint[] | null>(null)
  const [notice, setNotice] = useState('')

  useEffect(() => {
    let current = true
    listComplaints()
      .then((filed) => current && setComplaints(filed))
      .catch((error) => current && setNotice(error instanceof Error ? error.message : 'The docket could not be read.'))
    return () => {
      current = false
    }
  }, [])

  return (
    <FormSheet>
      <SectionBand title="Docket of complaints received" />

      {notice ? (
        <p className="px-4 py-8 font-form text-base font-semibold text-stamp sm:px-7">{notice}</p>
      ) : !complaints ? (
        <p className="px-4 py-16 text-center font-form text-[11px] uppercase tracking-[0.22em] text-carbon/50 sm:px-7">
          Retrieving docket…
        </p>
      ) : complaints.length === 0 ? (
        <div className="px-4 py-12 text-center sm:px-7">
          <p className="font-display text-lg uppercase tracking-[0.08em] text-carbon">
            The docket is empty.
          </p>
          <p className="mt-2 font-form text-sm text-carbon/70">
            No complaints have been filed.
          </p>
        </div>
      ) : (
        <>
          <p className="px-4 pb-1 pt-4 font-form text-[11px] uppercase tracking-[0.16em] text-carbon/55 sm:px-7">
            {complaints.length} {complaints.length === 1 ? 'complaint' : 'complaints'} on file,
            most recently filed first
          </p>
          <ol className="space-y-4 px-4 pb-6 pt-2 sm:px-7">
            {complaints.map((complaint) => (
              <li key={complaint.id}>
                <DocketEntry complaint={complaint} />
              </li>
            ))}
          </ol>
        </>
      )}
    </FormSheet>
  )
}

function DocketEntry({ complaint }: { complaint: Complaint }) {
  return (
    <article className="border border-carbon/25">
      <header className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 border-b border-carbon/20 bg-process/[0.07] px-3 py-1.5">
        <p className="break-all font-type text-[12px] font-bold text-process">{complaint.caseNumber}</p>
        <p className="font-form text-[10px] uppercase tracking-[0.16em] text-carbon/60">
          Received {officialDate(complaint.createdAt)}
        </p>
      </header>

      <dl className="flex flex-wrap gap-x-6 gap-y-1 px-3 pt-2.5">
        <Field label="Complainant" value={complaint.filedBy} />
        <Field label="Respondent" value={complaint.respondent} />
        <Field label="Status" value={complaint.status} />
        <Field label="Estimated review time" value={complaint.reviewEstimate} />
      </dl>

      {/* Shown on the same printed rules it was typed onto. */}
      <p className="ruled-field mx-3 mb-3 mt-2.5 whitespace-pre-wrap break-words border border-carbon/15 px-2 font-type text-[14px] text-carbon">
        {complaint.complaintText}
      </p>
    </article>
  )
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="font-form text-[9px] uppercase tracking-[0.16em] text-carbon/55">{label}</dt>
      <dd className="font-type text-[13px] text-carbon">{value}</dd>
    </div>
  )
}
